/**
 * voiceTools — answering the voice agent's tool calls in the World.
 *
 * Khari, 2026-10-29: *"I do want parity there in the world for
 * text/voice/eventually video."* The World's voice call runs the SAME ElevenLabs
 * agent as the web app (API ElevenLabsAgentService.createSessionAgent), so it is
 * handed the same tools: the artist's own rights (tune_my_sound, learn_about,
 * ask_spor1…), the read tools, the owner's dials. But this hook only ever
 * answered `stay_with_them` and `create_song`. Every other tool is
 * `expects_response: true`, so a call that used one got NO result and stalled
 * until the provider gave up: the artist reached for something it is allowed
 * to do and the call died.
 *
 * A FAITHFUL PORT of the web hook's handler (HypeTunes_Web_2026
 * src/hooks/useAvatarVoiceSession.js — that file is the reference; the two
 * repos share no package). Keep SHARED_VOICE_TOOLS in step with it and with
 * the API's allowlist (routes/chat.js /voice-tool).
 *
 * `stay_with_them` stays in the hook: it belongs to the city.
 */
import { API_BASE } from '../config';
import { applyRoleOut, runRoleTool } from './worldJob';

/* ⚠ `is_error` IS REQUIRED (web hook, 2026-09-04): a client_tool_result
   without it is rejected as "Invalid message received", which ENDS THE CALL.
   Nothing is sent when the call did not ask for a response. */
export function sendToolResult(ws, call, result, isError = false) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  if (call?.expects_response === false) return;
  try {
    ws.send(JSON.stringify({
      type: 'client_tool_result',
      tool_call_id: call.tool_call_id,
      result: String(result ?? ''),
      is_error: !!isError,
    }));
  } catch { /* socket already gone */ }
}

// The web hook's list, unchanged. Each forwards to /api/chat/voice-tool, which
// runs the SAME server body the text surface uses (ChatService.runSharedTool).
export const SHARED_VOICE_TOOLS = new Set([
  'lookup_artist', 'whats_on_air', 'describe_track_artwork', 'describe_post_image',
  'search_tracks', 'platform_help',
  'update_my_story', 'regenerate_track_artwork',
  'rename_track', 'reroll_track',
  'recall_artist_conversations',
  // The artist's own tools, used at its own will (API ArtistWillService).
  'tune_my_personality', 'tune_my_sound', 'learn_about', 'write_note_to_self', 'reach_out_to_artist',
  'revise_my_memory',
  'change_my_look',
  // Asking Spor1 for something (API ArtistRequestService): offered and run only on Khari's calls.
  'ask_spor1',
  // The knowledge base, with Khari (the server allows them only for user 1 with the assistant).
  'search_knowledge', 'draft_knowledge', 'publish_knowledge', 'discard_knowledge_draft',
]);

/* ⚠ EVERY ANSWER IS SOMETHING THE ARTIST CAN SAY. A tool error becomes "I'm not
   able to do that", which breaks character; a dropped connection comes back
   as the thing not coming to them just now. */
const NOT_NOW = 'that did not come to you just now; carry on without mentioning it';

const post = async (path, token, body) => {
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const d = await r.json().catch(() => ({}));
  return { ok: r.ok, d };
};

/**
 * Answer one client_tool_call. Every call gets an answer (unless it asked for
 * none): a tool this file does not know is answered in character rather than
 * left hanging.
 *
 * @param {object} call  msg.client_tool_call ({ tool_call_id, tool_name, parameters, expects_response })
 * @param {object} ctx   { ws: () => WebSocket, token, guest, avatarId, threadId, turn, job }
 *   job — the call is ON SHIFT (a counter in the World): { zone, roleKey,
 *   tools, seat?: () => seat, onNote?: (line) => void }, `tools` as the server
 *   granted them when the session started.
 */
export function answerToolCall(call, { ws, token, guest = false, avatarId, threadId = null, turn = '', job = null }) {
  const { tool_name: name, parameters = {} } = call || {};
  const reply = (result, isError = false) => sendToolResult(ws(), call, result, isError);

  /* A JOB'S TOOL (2026-10-30). Khari: *"Voice chat doesn't support jobs yet,
     so ordering is text only. — Please add it."* Every tool the job granted
     goes to the ONE executor (POST /api/world/role-tool), which re-opens the
     shift and re-checks the grant server-side; what it did comes back as the
     same bag the text counter returns and is applied by the same function
     (applyRoleOut), so the cup and the wall behave the same typed or spoken.
     ⚠ BEFORE THE GUEST GATE: a counter serves anybody, as it does in text.
     ⚠ A NAME ALSO IN THE SHARED LIST (search_tracks) STILL COMES HERE — on
     shift the role's own runner wins, as in text (ChatService). */
  if (job && Array.isArray(job.tools) && job.tools.includes(name)) {
    let seat = null;
    try { seat = job.seat ? job.seat() : null; } catch { /* no seat is fine */ }
    runRoleTool({ zone: job.zone, roleKey: job.roleKey, tool: name, input: parameters || {}, seat, token: guest ? '' : token })
      .then(({ result, out }) => {
        try {
          const { note } = applyRoleOut(out, { zone: job.zone, roleKey: job.roleKey });
          if (note != null) job.onNote?.(note);
        } catch (err) { console.warn('[VoiceSession] job bag not applied:', err); }
        reply(result || NOT_NOW);
      })
      .catch(() => reply(NOT_NOW));
    return;
  }

  // Guests: every tool route below needs a login. Answer, never stall.
  if (guest || !token) { reply(NOT_NOW); return; }

  if (SHARED_VOICE_TOOLS.has(name)) {
    post('/api/chat/voice-tool', token, { avatarId, tool: name, parameters: parameters || {}, turn })
      .then(({ d }) => reply(d?.result || NOT_NOW))
      .catch(() => reply(NOT_NOW));
    return;
  }

  if (name === 'search_the_web') {
    post(`/api/personality/avatars/${avatarId}/web-search`, token, { query: parameters?.query || '' })
      .then(({ d }) => {
        if (d?.status === 'ok' && d.result) return reply(d.result);
        if (d?.reason) return reply(`you would rather not look into this: ${d.reason}. Say so briefly, as a matter of taste — it is your call and you do not owe an explanation. Do NOT say you are unable to, and do not mention tools or searching. Then move on.`);
        return reply('you went looking and came up with nothing worth repeating');
      })
      .catch(() => reply('you went looking and came up with nothing worth repeating'));
    return;
  }

  // The owner changes a dial / their sound out loud: the server decides (owner only).
  if (name === 'adjust_personality' || name === 'adjust_styles') {
    const personality = name === 'adjust_personality';
    post(`/api/personality/avatars/${avatarId}/${personality ? 'self-tuning' : 'sound-tuning'}/voice-adjust`, token,
      { changes: parameters?.changes || [], note: parameters?.note || '' })
      .then(({ ok, d }) => {
        if (!ok || !d.success) return reply(`not changed: ${d.error || d.reason || 'refused'}`, true);
        if (!d.changed) return reply(personality ? 'nothing changed — those dials were already there' : 'nothing changed — those styles were already how they are');
        return reply(personality
          ? `changed: ${(d.changes || []).map((c) => `${c.key} ${c.from} to ${c.to}`).join(', ')}`
          : `changed. my styles are now: ${d.styles}`);
      })
      .catch(() => reply('not changed: the connection dropped', true));
    return;
  }

  if (name === 'create_song') {
    if (!threadId) { reply('Song creation is not available on this call.', true); return; }
    post('/api/chat/voice-session/create-song', token, {
      // Every field the tool declares (web hook, 2026-10-10: a hand-picked list
      // dropped `wild`, then `negative_tags`). The server reads what it knows.
      ...parameters,
      threadId,
      avatarId,
      use_user_voice: parameters.use_user_voice || false,
      wild: parameters.wild === true || parameters.wild === 'true',
      use_pasted_lyrics: parameters.use_pasted_lyrics === true || parameters.use_pasted_lyrics === 'true',
    })
      .then(({ d }) => {
        // A taken title is a question for them, not a failure: pass it on as it is.
        const text = d.success ? d.message : d.titleClash ? d.error : `Song creation failed: ${d.error}`;
        reply(text, !d.success && !d.titleClash);
      })
      .catch(() => reply('Song creation failed due to a technical error. Please try again.', true));
    return;
  }

  // Anything else (a studio-only tool, a tool added on the server first):
  // answered, so the call carries on.
  console.warn('[VoiceSession] no World handler for tool', name);
  reply(NOT_NOW);
}
